import { Component, computed, OnInit, signal } from '@angular/core';
import { Store } from '@ngrx/store';
import { fadeAnimation } from '../../../core/animations/fade.animation';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ApiError } from '../../../core/models/common/api-error';
import { FormValidationService } from '../../../core/services/form-validation.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { selectAuthUser, selectAuthVendor } from '../../../core/store/reducers';
import { VendorsService } from '../../../core/services/vendors.service';
import { authActions } from 'angular/app/core/store/auth';
import { ToastService } from '../../../core/services/toast.service';

@Component({
  selector: 'app-calendar-constraints-page',
  animations: [fadeAnimation],
  template: `
    <!-- Page Header -->
    <app-page-header title="Ouverture du calendrier"></app-page-header>

    <app-spacer size="6"></app-spacer>

    <app-fieldset>
      <div title>Ouverture du calendrier</div>
      <div description
        >Définissez une ouverture maximale du calendrier de vos salles dans le
        temps.</div
      >

      <div>
        <app-text-input
          label="Limiter l’ouverture du calendrier à"
          name="interval_duration"
          [control]="form.controls.interval_duration"
          [helpText]="helpText()"
          [appendOptions]="appendOptions"
          [appendOptionValue]="form.value.interval_type"
          (appendOptionValueChange)="
            form.controls.interval_type.patchValue($any($event))
          "
        />
        <app-button
          (click)="submit()"
          [loading]="sendingData()"
          variant="secondary"
          >Enregistrer</app-button
        >
      </div>
    </app-fieldset>
  `,
  styles: [],
  standalone: false,
})
export class CalendarConstraintsPageComponent implements OnInit {
  form = new FormGroup({
    interval_duration: new FormControl('', [
      Validators.pattern('^[0-9]*$'),
      Validators.min(1),
    ]),
    interval_type: new FormControl(''),
  });
  formValue = toSignal(this.form.valueChanges);
  sendingData = signal(false);
  helpText = computed(() => {
    if (this.formValue().interval_duration?.length) {
      const intervalType = this.appendOptions.find(
        (el) => el.value === this.formValue().interval_type,
      ).label;
      return `Les clients pourront réserver vos salles jusqu’à ${this.formValue().interval_duration} ${intervalType} à l’avance.`;
    } else {
      return 'Les clients pourront réserver vos salles sans limite de date.';
    }
  });
  vendor = this.store.selectSignal(selectAuthVendor);
  interval = computed(() => {
    if (this.formValue().interval_duration?.length) {
      return `P${this.formValue().interval_duration}${this.formValue().interval_type}`;
    } else {
      return null;
    }
  });
  appendOptions = [
    { label: 'an(s)', value: 'Y' },
    { label: 'mois', value: 'M' },
    { label: 'jour(s)', value: 'D' },
  ];

  constructor(
    private store: Store,
    private vendorsService: VendorsService,
    private formValidationService: FormValidationService,
    private toastService: ToastService,
  ) {}

  ngOnInit(): void {
    const duration = this.vendor().booking_period_constraint?.length
      ? this.vendor().booking_period_constraint.match(/\d+/)[0]
      : '';
    const intervalType = this.vendor().booking_period_constraint?.length
      ? this.vendor().booking_period_constraint[
          this.vendor().booking_period_constraint.length - 1
        ]
      : 'Y';
    this.form.patchValue({
      interval_duration: duration,
      interval_type: intervalType,
    });
  }

  async submit() {
    try {
      this.sendingData.set(true);
      const vendor = await this.vendorsService.updateBookingPeriodConstraint({
        booking_period_constraint: this.interval(),
      });
      this.store.dispatch(
        authActions.findCurrentUserSuccess({
          user: { ...this.store.selectSignal(selectAuthUser)(), vendor },
        }),
      );
      this.toastService.success('Modification enregistrée.');
    } catch (exception) {
      this.formValidationService.displayApiValidationErrors(
        this.form,
        exception as ApiError,
      );
    } finally {
      this.sendingData.set(false);
    }
  }
}
